<template>
  <div
    v-if="show"
    class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
    @click.self="$emit('close')"
  >
    <div class="bg-white rounded-lg p-6 max-w-md w-full mx-4">
      <h3 class="text-lg font-semibold text-gray-900 mb-4">View Details</h3>

      <div v-if="item" class="space-y-4">
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1"
            >Query</label
          >
          <p class="text-sm text-gray-900">{{ item.query }}</p>
        </div>
        <div v-if="programName">
          <label class="block text-xs font-medium text-gray-500 mb-1"
            >Program</label
          >
          <p class="text-sm text-gray-900">{{ programName }}</p>
        </div>
        <div>
          <label class="block text-xs font-medium text-gray-500 mb-1"
            >Added on</label
          >
          <p class="text-sm text-gray-900">
            {{ formatWatchlistDate(item.addedAt) }}
          </p>
        </div>
        <div>
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Update frequency
          </label>
          <select
            v-model="localFrequency"
            class="w-full px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select>
        </div>
      </div>

      <div class="flex justify-end gap-2 mt-6">
        <button
          @click="$emit('close')"
          class="px-4 py-2 text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
        <button
          @click="handleSave"
          :disabled="saving"
          class="px-4 py-2 bg-primary-purple text-white rounded-md hover:bg-primary-purple/90 disabled:opacity-50"
        >
          {{ saving ? "Saving..." : "Save" }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch } from "vue";
import { useMutation } from "@vue/apollo-composable";
import gql from "graphql-tag";

const UPDATE_WATCHLIST_MUTATION = gql`
  mutation UpdateTranscriptWatchlist($input: UpdateTranscriptWatchlistInput!) {
    updateTranscriptWatchlist(input: $input) {
      watchlistItem {
        id
        query
        frequency
        currentResultCount
        resultsChanged
      }
      errors
    }
  }
`;

const LIST_WATCHLIST_QUERY = gql`
  query ListTranscriptWatchlist($limit: Int) {
    listTranscriptWatchlist(limit: $limit) {
      id
      query
      programId
      frequency
      addedAt
      initialResultCount
      previousResultCount
      currentResultCount
      resultsChanged
      lastRunAt
    }
  }
`;

export interface WatchlistItem {
  id: string;
  query: string;
  programId?: string | null;
  frequency: string;
  addedAt: string;
  initialResultCount: number;
  currentResultCount: number;
  resultsChanged: boolean;
}

export default defineComponent({
  name: "WatchlistDetailsModal",
  props: {
    show: { type: Boolean, default: false },
    item: { type: Object as () => WatchlistItem | null, default: null },
    programName: { type: String, default: "" },
  },
  emits: ["close", "updated"],
  setup(props, { emit }) {
    const localFrequency = ref(props.item?.frequency ?? "weekly");
    const { mutate: updateWatchlist, loading: saving } = useMutation(
      UPDATE_WATCHLIST_MUTATION,
      { refetchQueries: [{ query: LIST_WATCHLIST_QUERY }] }
    );

    watch(
      () => props.item,
      (item) => {
        localFrequency.value = item?.frequency ?? "weekly";
      },
      { immediate: true }
    );

    const formatWatchlistDate = (dateStr: string) => {
      if (!dateStr) return "";
      return new Date(dateStr).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    };

    const handleSave = async () => {
      if (!props.item) return;
      await updateWatchlist({
        input: {
          id: props.item.id,
          frequency: localFrequency.value,
        },
      });
      emit("updated");
      emit("close");
    };

    return {
      localFrequency,
      saving,
      formatWatchlistDate,
      handleSave,
    };
  },
});
</script>
